import { useColorMode } from "@chakra-ui/react";
import { TcmEncounter, TcmEncounterResponse } from "../../../api/tcm-encounter";
import { getGridClassName } from "../../shared/ag-grid";
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  IDatasource,
  IGetRowsParams,
  InfiniteRowModelModule,
  ModuleRegistry,
  RowClickedEvent,
  SortChangedEvent,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import {
  ForwardedRef,
  MutableRefObject,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { formatDateTimeInLocalNoYear } from "../../shared/date";
import { GetTcmEncounterQueryParams, TocSortState } from "./filters";
import { LatestStatusCell } from "./latest-status-cell";
import { TextCellEditor } from "./text-cell-editor";

ModuleRegistry.registerModules([InfiniteRowModelModule]);

/** Maps row number to page URL */
type RowMapping = Record<string, string>;

export type TransitionsOfCareGridRef = {
  resetSearch: (filters: GetTcmEncounterQueryParams) => void;
  updatePage: (filters: GetTcmEncounterQueryParams) => void;
  api: GridApi | null;
};

export type TransitionsOfCareGridProps = {
  getTcmEncounters: (
    pageUrl?: string | undefined,
    filters?: GetTcmEncounterQueryParams | undefined
  ) => Promise<TcmEncounterResponse | undefined>;
  initialFilters?: GetTcmEncounterQueryParams;
  initialSort?: TocSortState;
  isLoading: boolean;
  numItemsPerPage?: number | undefined;
  onRowClick?: (row: TcmEncounter) => void;
  onRowsLoadedRef?: MutableRefObject<(rows: TcmEncounter[]) => void>;
  setEventTypeFilter?: (eventType: "Admitted" | "Discharged" | "") => void;
  onNoteUpdate?: (encounterId: string, note: string) => void;
  onSortChange?: (sortState?: TocSortState) => void;
};

type GridState = {
  rowMapping: RowMapping;
  filters: GetTcmEncounterQueryParams;
};
const initialState: GridState = { rowMapping: {}, filters: {} };

const TransitionsOfCareGrid = forwardRef<
  TransitionsOfCareGridRef,
  React.PropsWithChildren<TransitionsOfCareGridProps>
>(
  (
    {
      getTcmEncounters,
      initialFilters,
      initialSort,
      isLoading,
      numItemsPerPage = 5,
      onRowClick,
      onRowsLoadedRef,
      setEventTypeFilter,
      onNoteUpdate,
      onSortChange,
    }: TransitionsOfCareGridProps,
    ref: ForwardedRef<TransitionsOfCareGridRef>
  ) => {
    const { colorMode } = useColorMode();
    const [api, setApi] = useState<GridApi | null>(null);
    const [gridState] = useState<GridState>(() => ({
      ...initialState,
      filters: initialFilters ?? {},
    }));

    const columnDefs: ColDef[] = useMemo(() => {
      return [
        { field: "id", hide: true },
        { field: "patientLastName", headerName: "Last Name", sortable: true },
        { field: "patientFirstName", headerName: "First Name", sortable: true },
        { field: "patientDateOfBirth", headerName: "DOB", sortable: false },
        { field: "facilityName", headerName: "Admitting Facility", sortable: false },
        {
          field: "class",
          headerName: "Class",
          sortable: false,
          cellStyle: (params: { value: string }) => {
            return params.value === "emergency" || params.value === "inpatient encounter"
              ? { backgroundColor: colorMode === "dark" ? "#ef93a1" : "#ffebee" }
              : { backgroundColor: colorMode };
          },
        },
        {
          field: "admitTime",
          headerName: "Admit Date",
          sortable: true,
          cellRenderer: (params: { value: Date | null | undefined }) =>
            formatDateTimeInLocalNoYear(params.value),
        },
        {
          field: "dischargeTime",
          headerName: "Discharge Date",
          sortable: true,
          cellRenderer: (params: { value: Date | null | undefined }) =>
            formatDateTimeInLocalNoYear(params.value),
        },
        {
          field: "latestStatus",
          headerName: "Latest Status",
          sortable: false,
          cellRenderer: LatestStatusCell,
        },
        {
          field: "freetextNote",
          headerName: "Notes",
          editable: true,
          cellRenderer: (params: { value: string | null | undefined }) => params.value ?? "",
          cellClass: "editable-cell",
          singleClickEdit: true,
          cellEditorPopup: true,
          cellEditor: TextCellEditor,
          cellEditorParams: { onNoteUpdate },
        },
      ];
    }, [colorMode, onNoteUpdate]);

    const defaultColDef: ColDef = {
      flex: 1,
    };

    const getRows = useCallback(
      (gridApi: GridApi) => async (params: IGetRowsParams) => {
        try {
          const { gridState: localGridState }: { gridState: GridState } = params.context;
          const currPageNumber = gridApi.paginationGetCurrentPage();
          const currPageUrl = (localGridState.rowMapping ?? {})[currPageNumber];
          const filters = localGridState.filters;

          const admitTimeSort = gridApi.getColumnState().find(c => c.colId === "admitTime")?.sort;
          const dischargeTimeSort = gridApi
            .getColumnState()
            .find(c => c.colId === "dischargeTime")?.sort;
          const lastOutreachDateSort = gridApi
            .getColumnState()
            .find(c => c.colId === "lastOutreachDate")?.sort;
          const patientFirstNameSort = gridApi
            .getColumnState()
            .find(c => c.colId === "patientFirstName")?.sort;
          const patientLastNameSort = gridApi
            .getColumnState()
            .find(c => c.colId === "patientLastName")?.sort;

          const resp = await getTcmEncounters(currPageUrl, {
            ...filters,
            ...(admitTimeSort ? { admitTimeSort } : {}),
            ...(dischargeTimeSort ? { dischargeTimeSort } : {}),
            ...(lastOutreachDateSort ? { lastOutreachDateSort } : {}),
            ...(patientFirstNameSort ? { patientFirstNameSort } : {}),
            ...(patientLastNameSort ? { patientLastNameSort } : {}),
          });
          if (!resp) {
            params.failCallback();
            return;
          }
          const { items: encounters, meta } = resp;
          if (!encounters) {
            params.failCallback();
            return;
          }

          const totalCountOfItems = getTotalCountOfItems(meta, currPageNumber, numItemsPerPage);
          params.successCallback(encounters, totalCountOfItems);
          onRowsLoadedRef?.current?.(encounters);
          updateRowMapping(meta, currPageNumber, localGridState);
        } catch (error) {
          params.failCallback();
        }
      },
      [getTcmEncounters, numItemsPerPage, onRowsLoadedRef]
    );

    const onGridReady = useCallback(
      (params: GridReadyEvent) => {
        if (initialSort?.sortBy && initialSort.sortDir) {
          params.api.applyColumnState({
            state: [{ colId: initialSort.sortBy, sort: initialSort.sortDir }],
            defaultState: { sort: null },
          });
        }

        const dataSource: IDatasource = {
          rowCount: undefined,
          getRows: getRows(params.api),
        };
        params.api.setGridOption("datasource", dataSource);
        setApi(params.api);
      },
      [getRows, initialSort]
    );

    useImperativeHandle(ref, () => ({
      resetSearch(filters: GetTcmEncounterQueryParams) {
        if (api) {
          gridState.filters = filters;
          gridState.rowMapping = {};
          api.paginationGoToPage(0);
          api.purgeInfiniteCache();
        }
      },
      updatePage(filters: GetTcmEncounterQueryParams) {
        if (api) {
          gridState.filters = filters;
          api.purgeInfiniteCache();
        }
      },
      api,
    }));

    const handleRowClick = useCallback(
      (event: RowClickedEvent) => {
        const clickedElement = event.event?.target as HTMLElement;
        const hasNoRowClick = clickedElement.closest(".editable-cell");

        if (hasNoRowClick) {
          return;
        }

        onRowClick?.(event.data);
      },
      [onRowClick]
    );

    const handleSortChanged = useCallback(
      (event: SortChangedEvent) => {
        const currentSort = getCurrentSort(event.api);
        const dischargeTimeSort = event.api
          .getColumnState()
          .find(c => c.colId === "dischargeTime")?.sort;

        if (setEventTypeFilter) {
          if (dischargeTimeSort) {
            gridState.filters.type = "Discharged";
            setEventTypeFilter("Discharged");
          } else {
            gridState.filters.type = "";
            setEventTypeFilter("");
          }
        }

        onSortChange?.(currentSort);
      },
      [gridState.filters, onSortChange, setEventTypeFilter]
    );

    return (
      <>
        <style>{`
          .cursor-pointer { cursor: pointer; }
          .ag-popup-editor { transform: translateY(9px); box-shadow: none !important; }
          .ag-popup-editor input { padding: 0; font-size: inherit; font-weight: inherit; }
          .ag-popup-editor input:focus-visible { border: none; box-shadow: none; }
        `}</style>
        <AgGridReact
          cacheBlockSize={numItemsPerPage}
          cacheOverflowSize={1}
          className={getGridClassName(colorMode)}
          columnDefs={columnDefs}
          context={{ gridState }}
          defaultColDef={defaultColDef}
          enableCellTextSelection={true}
          getRowId={params => params.data.id}
          headerHeight={60}
          infiniteInitialRowCount={0}
          loading={isLoading}
          maxBlocksInCache={1}
          maxConcurrentDatasourceRequests={1}
          onGridReady={onGridReady}
          onRowClicked={handleRowClick}
          onSortChanged={handleSortChanged}
          pagination={true}
          paginationPageSize={numItemsPerPage}
          paginationPageSizeSelector={false}
          rowClass="cursor-pointer"
          rowModelType={"infinite"}
          suppressCellFocus={true}
          suppressMultiSort={true}
        />
      </>
    );
  }
);

function updateRowMapping(
  paginationMeta: { nextPage?: string; prevPage?: string; itemsOnPage: number },
  currPageNumber: number,
  myState: GridState
): void {
  const nextPageNumber = currPageNumber + 1;
  const nextPageUrl = paginationMeta.nextPage;
  const rowMappingUpdate: RowMapping = {
    ...(nextPageNumber >= 0 && nextPageUrl ? { [nextPageNumber]: nextPageUrl } : {}),
  };
  if (currPageNumber === 1 && paginationMeta.prevPage) {
    rowMappingUpdate[0] = paginationMeta.prevPage;
  }
  const newRowMapping = { ...myState.rowMapping, ...rowMappingUpdate };
  myState.rowMapping = newRowMapping;
}

function getTotalCountOfItems(
  paginationMeta: { nextPage?: string; itemsOnPage: number },
  currPageNumber: number,
  numItemsPerPage: number
): number | undefined {
  if (!paginationMeta.nextPage) {
    const totalNumItems = paginationMeta.itemsOnPage + currPageNumber * numItemsPerPage;
    return totalNumItems;
  }
  return -1;
}

export default TransitionsOfCareGrid;

function getCurrentSort(gridApi: GridApi): TocSortState | undefined {
  const sortedColumn = gridApi.getColumnState().find(column => column.sort);

  if (!sortedColumn || (sortedColumn.sort !== "asc" && sortedColumn.sort !== "desc")) {
    return undefined;
  }

  const supportedSortFieldIds: TocSortState["sortBy"][] = [
    "admitTime",
    "dischargeTime",
    "patientFirstName",
    "patientLastName",
  ];
  const sortBy = supportedSortFieldIds.find(field => field === sortedColumn.colId);

  if (!sortBy) {
    return undefined;
  }

  return {
    sortBy,
    sortDir: sortedColumn.sort,
  };
}
